import * as React from 'react'
import { toast } from 'sonner'
import type { TranscriptUtterance } from '@/client/types.gen'
import { parseSpeakersFromTranscript } from '@/components/project/artifact-viewer/transcript/utils'
import { useTranslation } from '@/hooks/useTranslation'

export interface UseSpeakerFilterParams {
	artifactId: string
	utterances: TranscriptUtterance[] | null
	editedSpeakers: Map<number, string>
	editedUtterances: Map<number, string>
	structuredSpeakers: string[] | null
	content: string
	updateUtterance: (index: number, text: string, newSpeaker?: string) => void
}

export interface UseSpeakerFilterReturn {
	speakers: string[]
	selectedSpeakers: Set<string>
	handleSpeakerToggle: (speaker: string) => void
	handleSelectAllSpeakers: () => void
	handleRenameSpeaker: (oldName: string, newName: string) => void
	handleBatchRenameSpeakers: (renames: Map<string, string>) => void
}

export function useSpeakerFilter({
	artifactId: _artifactId,
	utterances,
	editedSpeakers,
	editedUtterances,
	structuredSpeakers,
	content,
	updateUtterance,
}: UseSpeakerFilterParams): UseSpeakerFilterReturn {
	const { t } = useTranslation()
	const [selectedSpeakers, setSelectedSpeakers] = React.useState<Set<string>>(new Set())

	const speakers = React.useMemo(() => {
		let baseSpeakers: string[]
		if (structuredSpeakers && structuredSpeakers.length > 0) {
			baseSpeakers = structuredSpeakers
		} else {
			baseSpeakers = parseSpeakersFromTranscript(content)
		}

		if (utterances && utterances.length > 0 && editedSpeakers.size > 0) {
			const speakerSet = new Set<string>()
			for (let i = 0; i < utterances.length; i++) {
				const speaker = editedSpeakers.get(i) ?? utterances[i].speaker
				speakerSet.add(speaker)
			}
			return Array.from(speakerSet).sort()
		}

		return baseSpeakers
	}, [structuredSpeakers, content, utterances, editedSpeakers])

	React.useEffect(() => {
		setSelectedSpeakers(new Set())
	}, [])

	React.useEffect(() => {
		if (speakers.length > 0 && selectedSpeakers.size === 0) {
			setSelectedSpeakers(new Set(speakers))
		}
	}, [speakers, selectedSpeakers.size])

	const handleSpeakerToggle = React.useCallback((speaker: string) => {
		setSelectedSpeakers((prev) => {
			const next = new Set(prev)
			if (next.has(speaker)) {
				next.delete(speaker)
			} else {
				next.add(speaker)
			}
			return next
		})
	}, [])

	const handleSelectAllSpeakers = React.useCallback(() => {
		setSelectedSpeakers(new Set(speakers))
	}, [speakers])

	const handleRenameSpeaker = React.useCallback(
		(oldName: string, newName: string) => {
			const trimmed = newName.trim()
			if (!utterances || oldName === trimmed || !trimmed) return

			const otherSpeakers = new Set(speakers.filter((s) => s !== oldName))
			if (otherSpeakers.has(trimmed)) {
				toast.error(t('pages.projectDetail.transcript.speakerNameAlreadyUsed'))
				return
			}

			for (let i = 0; i < utterances.length; i++) {
				const currentSpeaker = editedSpeakers.get(i) ?? utterances[i].speaker
				if (currentSpeaker === oldName) {
					const currentText = editedUtterances.get(i) ?? utterances[i].text
					updateUtterance(i, currentText, trimmed)
				}
			}

			setSelectedSpeakers((prev) => {
				const next = new Set(prev)
				if (next.has(oldName)) {
					next.delete(oldName)
					next.add(trimmed)
				}
				return next
			})
		},
		[utterances, editedUtterances, editedSpeakers, speakers, updateUtterance, t]
	)

	const handleBatchRenameSpeakers = React.useCallback(
		(renames: Map<string, string>) => {
			if (!utterances || renames.size === 0) return

			const finalNames = speakers.map((s) => {
				const renamed = renames.get(s)
				return renamed ? renamed.trim() : s
			})
			const uniqueCount = new Set(finalNames).size
			if (uniqueCount < speakers.length) {
				toast.error(t('pages.projectDetail.transcript.speakerNameAlreadyUsed'))
				return
			}

			for (let i = 0; i < utterances.length; i++) {
				const currentSpeaker = editedSpeakers.get(i) ?? utterances[i].speaker
				const newName = renames.get(currentSpeaker)
				if (newName) {
					const currentText = editedUtterances.get(i) ?? utterances[i].text
					updateUtterance(i, currentText, newName.trim())
				}
			}

			setSelectedSpeakers((prev) => {
				const next = new Set(prev)
				for (const [oldName, newName] of renames) {
					if (next.has(oldName)) {
						next.delete(oldName)
						next.add(newName.trim())
					}
				}
				return next
			})
		},
		[utterances, editedUtterances, editedSpeakers, speakers, updateUtterance, t]
	)

	return {
		speakers,
		selectedSpeakers,
		handleSpeakerToggle,
		handleSelectAllSpeakers,
		handleRenameSpeaker,
		handleBatchRenameSpeakers,
	}
}
